<template>
	<div class="omapi-campaign-table__filters flex">
		<!-- Campaign Type Filter (multiselect) -->
		<campaigns-table-filter-dropdown
			v-model="campaignTypeValues"
			:options="campaignTypeFilterOptions"
			all-label="All Campaigns"
			:multiple="true"
		/>
		<!-- Sort Filter (single-select, no "all" item) -->
		<campaigns-table-filter-dropdown
			v-model="sortValue"
			:options="sortOptions"
			all-label="Newest"
			:show-all="false"
			:multiple="false"
			@input="handleSortFilter"
		/>
		<!-- Status Filter (multiselect) -->
		<campaigns-table-filter-dropdown
			v-model="statusValues"
			:options="statusFilterOptions"
			all-label="All Status"
			:multiple="true"
		/>
		<!-- Folder Filter (multiselect) -->
		<campaigns-table-filter-dropdown
			v-model="folderValues"
			:options="folderOptions"
			all-label="All Folders"
			:multiple="true"
			:disabled="!userFolders.length"
		/>
		<!-- Date Range Filter (single-select) -->
		<campaigns-table-filter-dropdown
			v-model="dateRange"
			:options="dateRangeOptions"
			all-label="All Time"
			:multiple="false"
		/>
		<!-- Unified date range picker — shown only when "Custom Range" is selected -->
		<core-form-datepicker-input
			v-if="'custom' === dateRange"
			class="omapi-filter-datepicker"
			:date-range="customDateRange"
			:open-immediate="true"
			align="right"
			@input="customDateRange = $event"
		/>
		<core-button size="large" :disabled="!canFilter" @click.prevent="handleFilter">Filter</core-button>
		<core-button v-show="canResetFilters" size="large" @click.prevent="clickReset">Clear</core-button>
	</div>
</template>

<script>
import { sub } from 'date-fns';
import { mapState, mapMutations, mapGetters, mapActions } from 'vuex';
import { dates } from '@/utils';

export default {
	data() {
		return {
			dateRange: '',
			customDateRange: [],
			campaignTypeValues: [],
			statusValues: [],
			folderValues: [],
			sortValue: 'newest',
			sortOptions: [
				{ value: 'newest', label: 'Newest' },
				{ value: 'oldest', label: 'Oldest' },
			],
			dateRangeOptions: [
				{ value: '7', label: 'Last 7 Days' },
				{ value: '30', label: 'Last 30 Days' },
				{ value: 'custom', label: 'Custom Range' },
			],
		};
	},
	computed: {
		...mapState('campaigns', ['folders']),
		...mapGetters('campaigns', ['canResetFilters', 'campaignTypeFilterOptions', 'statusFilterOptions']),
		userFolders() {
			return this.folders.filter((f) => 'dynamic' !== f.type);
		},
		folderOptions() {
			return this.userFolders.map((f) => ({ value: f.id, label: f.name }));
		},
		canFilter() {
			if ('custom' === this.dateRange) {
				return 2 === this.customDateRange.length;
			}
			return (
				this.canResetFilters ||
				this.campaignTypeValues.length ||
				this.statusValues.length ||
				this.folderValues.length ||
				this.dateRange
			);
		},
	},
	watch: {
		'$store.state.campaigns.sort'(newVal) {
			// When a column header sort is applied, reset the Newest/Oldest dropdown.
			if ('newest' !== newVal && 'oldest' !== newVal) {
				this.sortValue = 'newest';
			}
		},
	},
	created() {
		this.syncFromStore();
	},
	mounted() {
		this.$bus.$on('resetAllCampaignFilters', this.doReset);
	},
	beforeDestroy() {
		this.$bus.$off('resetAllCampaignFilters', this.doReset);
	},
	methods: {
		...mapMutations('campaigns', ['setFilterValues', 'updateSort']),
		...mapActions('campaigns', ['resetFilters', 'refreshDashboard', 'setPageVars']),
		/**
		 * Populate local filter state from the Vuex store so that UI stays
		 * correct when the component remounts during route changes (e.g. applying
		 * a search while filters are active navigates to a different named route,
		 * which causes a full remount and resets component data to its defaults).
		 *
		 * @returns {void}
		 */
		syncFromStore() {
			const { campaignType, status, folder, dateRange, starts, ends } = this.$store.state.campaigns.filters;
			const sort = this.$store.state.campaigns.sort;

			if (campaignType) {
				this.campaignTypeValues = campaignType
					.split(',')
					.map((v) => v.trim())
					.filter(Boolean);
			}
			if (status) {
				this.statusValues = status
					.split(',')
					.map((v) => v.trim())
					.filter(Boolean);
			}
			if (folder) {
				this.folderValues = folder
					.split(',')
					.map((v) => v.trim())
					.filter(Boolean);
			}
			if (dateRange) {
				this.dateRange = dateRange;
				if ('custom' === dateRange && starts && ends) {
					this.customDateRange = [new Date(starts * 1000), new Date(ends * 1000)];
				}
			}
			if ('newest' === sort || 'oldest' === sort) {
				this.sortValue = sort;
			}
		},
		/**
		 * Apply all active filters in a single action.
		 *
		 * @returns {void}
		 */
		handleFilter() {
			// Pass a no-op handler to prevent resetFilters from triggering its own refresh;
			// a single refreshDashboard() call at the end handles all filter changes together.
			const noRefresh = () => {};

			this.setFilterValues({ gamified: this.campaignTypeValues.includes('gamified') });

			if (this.campaignTypeValues.length) {
				this.setFilterValues({ campaignType: this.campaignTypeValues.join(',') });
			} else {
				this.resetFilters({ keys: ['campaignType'], handler: noRefresh });
			}

			if (this.statusValues.length) {
				this.setFilterValues({ status: this.statusValues.join(',') });
			} else {
				this.resetFilters({ keys: ['status'], handler: noRefresh });
			}

			if (this.folderValues.length) {
				this.setFilterValues({ folder: this.folderValues.join(',') });
			} else {
				this.resetFilters({ keys: ['folder'], handler: noRefresh });
			}

			if ('custom' === this.dateRange && 2 === this.customDateRange.length) {
				const [start, end] = this.customDateRange;
				const endOfDay = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59);
				this.setFilterValues({
					dateRange: 'custom',
					starts: dates.timestamp(start),
					ends: dates.timestamp(endOfDay),
				});
			} else if (this.dateRange && 'custom' !== this.dateRange) {
				this.setFilterValues({
					dateRange: this.dateRange,
					starts: dates.timestamp(sub(Date.now(), { days: this.dateRange })),
					ends: dates.timestamp(),
				});
			} else {
				this.resetFilters({ keys: ['dateRange', 'starts', 'ends'], handler: noRefresh });
			}

			this.setPageVars({ page: 1 });
			this.refreshDashboard().catch(() => {});
		},
		/**
		 * Sort Campaigns
		 *
		 * @returns {void}
		 */
		handleSortFilter() {
			this.updateSort({ value: this.sortValue });
			this.setPageVars({ page: 1 });
			this.refreshDashboard().catch(() => {});
		},
		clickReset() {
			this.doReset();
			this.updateSort({ value: 'newest' });
			return this.resetFilters();
		},
		doReset() {
			this.dateRange = '';
			this.customDateRange = [];
			this.campaignTypeValues = [];
			this.statusValues = [];
			this.folderValues = [];
			this.sortValue = 'newest';
		},
	},
};
</script>

<style lang="scss">
@import '../../../assets/scss/variables';

.omapi-screen {
	.omapi-campaign-table__filters {
		.omapi-button {
			margin-left: 0;
			margin-right: 0;
		}

		// Override the default bold on primary buttons for filter/clear actions
		.omapi-button.omapi-button__primary {
			font-weight: $font_normal;
		}
	}

	.omapi-filter-datepicker {
		.el-date-editor--daterange {
			height: 30px;
			width: auto;
			padding: 0 8px;
			border-color: $color_grey-border;
			border-radius: 3px;
			font-size: 13px;
			line-height: 30px;

			&:hover {
				border-color: darken($color_grey-border, 15%);
			}

			.el-range-input {
				font-size: 13px;
				color: $font_dark-color;
				width: 95px;
			}

			.el-range-separator {
				line-height: 30px;
				font-size: 13px;
				color: $font_secondary-color;
				padding: 0 4px;
			}

			.el-range__icon,
			.el-range__close-icon {
				line-height: 30px;
				font-size: 14px;
			}
		}
	}
}
</style>
